import React from 'react';
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { RocketCoin } from "@/components/icons/RocketCoin";
import { DripIcon } from "@/components/icons/Drip";

export function LaunchOnPancakeSwap() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="space-y-2 pb-8">
        <h1 className="text-4xl font-bold">Launch Your Token on Uniswap/PancakeSwap: The Ultimate Guide</h1>
        <p className="text-xl text-muted-foreground">
          Create and launch your token on Uniswap or PancakeSwap in minutes using our professional tools
        </p>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Published on December 18, 2024</span>
          <span>•</span>
          <span>Last Updated December 18, 2024</span>
          <span>•</span>
          <span>8 min read</span>
        </div>
      </div>

      <div className="space-y-8">
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Quick Navigation:</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Why Launch on Uniswap/PancakeSwap?</li>
            <li>Step 1: Create Your Token</li>
            <li>Step 2: Use Our Liquidity Manager</li>
            <li>Step 3: Launch Strategy</li>
            <li>Best Practices & Tips</li>
          </ul>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a 
            href="https://mdbapp.io/create"
            target="_blank"
            rel="noopener noreferrer"
            className="no-underline block"
          >
            <Button
              className="w-full mt-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white font-semibold py-6 text-lg"
            >
              <RocketCoin className="mr-2 h-5 w-5" />
              Create Your Token
            </Button>
          </a>

          <a 
            href="https://mdbapp.io/liquidity"
            target="_blank"
            rel="noopener noreferrer"
            className="no-underline block"
          >
            <Button
              className="w-full mt-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-6 text-lg"
            >
              <DripIcon className="mr-2 h-5 w-5" />
              Manage Liquidity
            </Button>
          </a>
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Why Launch on Uniswap/PancakeSwap?</h2>
          <p className="text-muted-foreground">
            Uniswap and PancakeSwap are the leading decentralized exchanges (DEX) on Ethereum and BNB Chain respectively, offering unparalleled exposure and liquidity opportunities for new tokens. With millions of active users and robust trading ecosystems, launching your token on either platform is your gateway to the crypto market.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Access to millions of potential investors</li>
            <li>Immediate trading capability</li>
            <li>Choose between Ethereum's security or BNB Chain's lower fees</li>
            <li>User-friendly interface for traders</li>
          </ul>
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Step 1: Create Your Token</h2>
          <p className="text-muted-foreground">
            Before launching on Uniswap/PancakeSwap, you need a professional, secure token contract. Our <a href="https://mdbapp.io/create" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Token Creator</a> makes this process simple and secure:
          </p>
          <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
            <li>Choose your token features (name, symbol, supply)</li>
            <li>Configure advanced options (tax, max transaction)</li>
            <li>Deploy with one click</li>
            <li>Get instant contract verification</li>
          </ol>
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Step 2: Use Our Liquidity Manager</h2>
          <p className="text-muted-foreground">
            Our platform features a powerful <a href="https://mdbapp.io/liquidity" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Liquidity Management Tool</a> that seamlessly integrates with both Uniswap and PancakeSwap:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>One-click liquidity addition to Uniswap/PancakeSwap</li>
            <li>Real-time price impact visualization</li>
            <li>Advanced liquidity locking options</li>
            <li>Professional liquidity management dashboard</li>
            <li>Instant trading activation on your chosen DEX</li>
          </ul>
          <div className="bg-blue-950/20 p-4 rounded-lg">
            <p className="text-blue-400">
              💡 Pro Tip: Use our liquidity manager to ensure a smooth and professional launch. It handles all the complex interactions with your chosen DEX for you!
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Step 3: Launch Strategy</h2>
          <p className="text-muted-foreground">
            A successful launch requires careful planning. Here's what you need to consider:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Time your launch for maximum visibility</li>
            <li>Prepare your marketing materials</li>
            <li>Engage with your community</li>
            <li>Monitor and adjust your strategy using our dashboard</li>
          </ul>
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Best Practices & Tips</h2>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>Use our platform to create a professional token contract</li>
            <li>Add sufficient initial liquidity through our liquidity manager</li>
            <li>Lock liquidity for at least 6 months to build trust</li>
            <li>Monitor your liquidity pools through our dashboard</li>
            <li>Keep your community updated on your progress</li>
          </ul>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gradient-to-r from-yellow-950/20 to-blue-950/20 p-6 rounded-lg">
          <div>
            <h3 className="text-xl font-semibold text-yellow-500">Create Your Token</h3>
            <p className="text-muted-foreground mt-2">
              Start with a professional-grade token contract that's ready for Uniswap/PancakeSwap.
            </p>
            <a 
              href="https://mdbapp.io/create"
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline block"
            >
              <Button
                className="w-full mt-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white font-semibold"
              >
                <RocketCoin className="mr-2 h-5 w-5" />
                Create Token
              </Button>
            </a>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-blue-500">Launch on DEX</h3>
            <p className="text-muted-foreground mt-2">
              Use our professional tools to manage your Uniswap/PancakeSwap liquidity.
            </p>
            <a 
              href="https://mdbapp.io/liquidity"
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline block"
            >
              <Button
                className="w-full mt-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold"
              >
                <DripIcon className="mr-2 h-5 w-5" />
                Manage Liquidity
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
